import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import 'rc-slider/assets/index.css';
import Header from '../../components/Header/Header';
import { useParams } from 'react-router-dom';
import { useService } from '../UseService';
import { useDispatch, useSelector } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import {
    capitalizeFirstLetter,
    formatDateAccordingToBrowser,
    isByHourCurrentPeriod,
    isPeriodSelectedInRangeOfCampaignPeriodType,
    formatNumber,
    dateToInt,
    getWholeCampaignPeriod,
} from '../../utils/functions';
import CampaignBrief from '../../components/DashboardIndividual/CampaignBrief/CampaignBrief';
import DropdownPeriods from '../../components/Select/DropdownPeriods';
import QuickControls from '../../components/DashboardIndividual/QuickControls/QuickControls';
import Predictions from '../../components/DashboardIndividual/Predictions/Predictions';
import ReportsTable from '../../components/Dashboard/ReportsTable/ReportsTable';
import DashboardStats from '../../components/DashboardIndividual/DashboardStats/DashboardStats';
import { dashboardActions, notificationsActions } from '../../store';
import { useLocalStorage } from '../../hooks/storage/useLocalStorage';
import statsService from '../../services/statsService';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import Icon from '../../components/Icon/Icon';
import PredictionDetails from '../../components/DashboardIndividual/Predictions/PredictionDetails/PredictionDetails';
import SpendPacingPanel from '../../components/DashboardIndividual/SpendPacing/SpendPacingPanel';
import ForecastConfidencePanel from '../../components/DashboardIndividual/ForecastConfidence/ForecastConfidencePanel';
import { isEvenlyMode } from '../../utils/spendPacing';
import { applyMarginToSpend } from '../../utils/margin';
import { useAuth } from '../UseAuth';
import { getForecastConfidenceRequestConfig } from '../../utils/forecastConfidence';

function DashboardPageIndividual() {
    const { t } = useTranslation();
    const service = useService();
    const dispatch = useDispatch();
    const { id } = useParams();
    const currencies = useSelector((state) => state.wallet.currencies);
    const dashboard = useSelector((state) => state.dashboard);
    const campaignPrediction = dashboard.campaignPrediction;
    const timezones = useSelector((state) => state.extra.timezones);
    const isCampaignPrediction = !!dashboard.campaignPrediction.screensDetails;
    const [currentPeriod, setCurrentPeriod] = useLocalStorage('currentPeriod', [
        null,
        null,
    ]);
    const individualCampaignStats = dashboard.individualCampaignStats;
    const campaign = dashboard.campaign;
    const isValidPrediction = JSON.stringify(campaignPrediction) !== '{}';
    const isActive = campaign.status === 5;
    const hasPredictions = isActive && isValidPrediction;
    const [isLoading, setIsLoading] = useState(dashboard.isLoading);
    const [reportsTable, setReportsTable] = useState([]);
    const [timezone, setTimezone] = useState({});
    const [graphicReport, setGraphicReport] = useState({});
    const [graphicOptions, setGraphicOptions] = useState({});
    const isDailyCapByBudget = !!campaign?.limit_type;
    const fadeOutAnimation = `transition-opacity delay-300 ${isLoading ? 'opacity-0' : 'opacity-1'
        }`;

    const [wholePeriodFrom, wholePeriodTo] = useMemo(
        () => getWholeCampaignPeriod(campaign),
        [
            campaign?.first_activation_at,
            campaign?.activated_at,
            campaign?.created_at,
            campaign?.start_period,
            campaign?.final_activity_at,
            campaign?.last_activity_at,
            campaign?.end_period,
            campaign?.updated_at,
            campaign?.status,
        ]
    );
    const [value, setValue] = useLocalStorage('dateName', 'today');
    const [totalRows, setTotalRows] = useState([]);
    const [campaignScreensStatus, setCampaignScreensStatus] = useState({});
    const [handleDataSchedule, setHandleDataSchedule] = useState(true);
    const isDelayed = campaignPrediction.endDate !== 'On time';
    const [showReport, setShowReport] = useState(true);
    const auth = useAuth();

    const [seeImpressions, setSeeImpressions] = useState(false);
    const [timelineMode, setTimelineMode] = useState('stats');
    const [forecastMetric, setForecastMetric] = useState('impacts');
    const [forecastTimezoneMode, setForecastTimezoneMode] = useState('utc');
    const [campaignPerformanceSeries, setCampaignPerformanceSeries] =
        useState([]);
    const [hasForecastData, setHasForecastData] = useState(false);
    const [hasForecastRequestError, setHasForecastRequestError] =
        useState(false);

    useEffect(() => {
        let adminOrSupervising = auth.isAdmin() || auth.isSupervising();
        setSeeImpressions(adminOrSupervising);
    }, []);

    const hasPendingOrRejected =
        campaignScreensStatus?.status?.pendingScreens?.total > 0 ||
        campaignScreensStatus?.status?.disapprovedScreens?.total > 0;

    const isCampaignLoaded =
        !!campaign.id && String(campaign.id) === String(id);

    useEffect(() => {
        if (id && !isCampaignLoaded && currencies.length > 0) {
            service.getCampaignPreview(id);
        }
    }, [id, isCampaignLoaded, currencies]);

    useEffect(() => {
        return () => {
            dispatch(dashboardActions.reset());
        };
    }, []);

    useEffect(() => {
        if (
            campaign.timezone_id &&
            timezones.length > 0 &&
            Object.keys(timezone).length === 0
        ) {
            let timezoneFound = timezones.find(
                (timezone) => timezone.id === campaign.timezone_id
            );
            setTimezone(timezoneFound);
        }
    }, [timezones, campaign]);

    // A custom period picked while looking at another campaign stays in
    // localStorage and means nothing here, so drop it instead of reporting it
    // as out of range. The named presets (today/yesterday/thisMonth/whole)
    // recalculate themselves for the current campaign and are left untouched.
    const normalizedPeriodForCampaign = useRef(null);
    useEffect(() => {
        if (!isCampaignLoaded) return;
        if (normalizedPeriodForCampaign.current === String(id)) return;
        normalizedPeriodForCampaign.current = String(id);
        if (value !== 'placeholder') return;
        if (!currentPeriod[0] || !currentPeriod[1]) return;
        const isOnRange = isPeriodSelectedInRangeOfCampaignPeriodType(
            currentPeriod,
            campaign,
            value
        );
        if (!isOnRange) setValue('today');
    }, [isCampaignLoaded, id, campaign, currentPeriod, value]);

    useEffect(() => {
        if (!isCampaignLoaded) return;
        const isOnRange = isPeriodSelectedInRangeOfCampaignPeriodType(
            currentPeriod,
            campaign,
            value
        );
        if (currentPeriod[0] && currentPeriod[1] && isOnRange) {
            service
                .getCampaignStats(campaign, currentPeriod)
                .then((res) => {
                    setTotalRows(res?.totals ?? []);
                })
                .catch(() => {
                    setTotalRows([]);
                });
            setIsLoading(false);
            setShowReport(true);
        } else {
            if (
                campaign.start_period &&
                campaign.end_period &&
                currentPeriod[0] &&
                currentPeriod[1] &&
                !isOnRange
            ) {
                dispatch(dashboardActions.removeIndividualCampaignStats());
                setShowReport(false);
                setGraphicReport({});
                const errorMessage = `${t(
                    'sections.dashboard-individual.period-out-of-range-description'
                )}, ${t(
                    'common.components.DateRangePicker.from'
                )}: ${formatDateAccordingToBrowser(campaign.start_period)} ${t(
                    'common.components.DateRangePicker.until'
                )}: ${formatDateAccordingToBrowser(campaign.end_period)}`;
                dispatch(
                    notificationsActions.setNotification({
                        type: 'warning',
                        title: t(
                            'sections.dashboard-individual.period-out-of-range-title'
                        ),
                        message: errorMessage,
                    })
                );
            }
        }
    }, [
        timezone,
        currentPeriod,
        value,
        isCampaignLoaded,
        campaign.id,
        campaign.start_period,
        campaign.end_period,
        wholePeriodFrom,
        wholePeriodTo,
    ]);

    // Update campaign on background every 2 minutes
    useEffect(() => {
        const cron = setInterval(async () => {
            // Only update if now is between start and end period
            if (
                dateToInt(currentPeriod[0]) <= dateToInt(Date.now()) &&
                dateToInt(currentPeriod[1]) >= dateToInt(Date.now())
            ) {
                setIsLoading(false);
                const isOnRange = isPeriodSelectedInRangeOfCampaignPeriodType(
                    currentPeriod,
                    campaign,
                    value
                );
                if (
                    isCampaignLoaded &&
                    currentPeriod[0] &&
                    currentPeriod[1] &&
                    isOnRange
                ) {
                    dispatch(dashboardActions.setIsSilentLoading(true));
                    await service.getCampaignStats(campaign, currentPeriod);
                    dispatch(dashboardActions.setIsSilentLoading(false));
                }
            }
        }, 120_000);
        return () => {
            clearInterval(cron);
        };
    }, [
        currentPeriod,
        value,
        isCampaignLoaded,
        campaign.id,
        campaign.start_period,
        campaign.end_period,
        wholePeriodFrom,
        wholePeriodTo,
    ]);

    function filterOutRowsWithoutProperty(
        dataArray,
        primaryProperty,
        secondaryProperty
    ) {
        return dataArray.map((report) => {
            const filteredData = report.data.filter((item) => {
                if (!item[primaryProperty]) return false;

                if (
                    item[secondaryProperty] <= 0 ||
                    item[secondaryProperty] === '-'
                )
                    return false;

                return true;
            });
            return { ...report, data: filteredData };
        });
    }

    const parseCampaignPerformanceSeries = (response) => {
        const rawSeries =
            response?.data?.data?.series || response?.data?.series || [];

        return rawSeries
            .map((item) => {
                const hour = String(item.hour || '00').padStart(2, '0');
                const utcDate = new Date(`${item.date}T${hour}:00:00Z`);
                return {
                    ...item,
                    hour,
                    utcDate,
                    actual_spent: Number(item.actual_spent || 0),
                    actual_impressions: Number(item.actual_impressions || 0),
                    actual_impacts: Number(item.actual_impacts || 0),
                    forecast_spent: Number(item.forecast_spent || 0),
                    forecast_impressions: Number(item.forecast_impressions || 0),
                    forecast_impacts: Number(item.forecast_impacts || 0),
                };
            })
            .filter((item) => !Number.isNaN(item.utcDate.getTime()))
            .sort((a, b) => a.utcDate - b.utcDate);
    };

    const getBrowserUtcOffsetLabel = () => {
        const offsetMinutes = -new Date().getTimezoneOffset();
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const absoluteMinutes = Math.abs(offsetMinutes);
        const hours = Math.floor(absoluteMinutes / 60);
        const minutes = absoluteMinutes % 60;

        if (minutes === 0) {
            return `UTC ${sign}${hours}`;
        }

        return `UTC ${sign}${hours}:${String(minutes).padStart(2, '0')}`;
    };

    const getBrowserTimezoneName = () => {
        const timezoneName =
            Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        return timezoneName;
    };

    const getForecastDisplayTimezoneLabel = () => {
        if (forecastTimezoneMode === 'utc') {
            return 'UTC';
        }

        const offsetLabel = getBrowserUtcOffsetLabel();
        const timezoneName = getBrowserTimezoneName();
        if (!timezoneName) {
            return offsetLabel;
        }

        return `${offsetLabel} (${timezoneName})`;
    };

    const filterPerformanceSeriesByCurrentPeriod = (series) => {
        if (!currentPeriod?.[0] || !currentPeriod?.[1]) {
            return series;
        }

        const fromDate = new Date(currentPeriod[0]);
        const toDate = new Date(currentPeriod[1]);

        if (
            Number.isNaN(fromDate.getTime()) ||
            Number.isNaN(toDate.getTime())
        ) {
            return series;
        }

        if (
            toDate.getHours() === 0 &&
            toDate.getMinutes() === 0 &&
            toDate.getSeconds() === 0
        ) {
            toDate.setHours(23, 59, 59, 999);
        }

        return series.filter(
            (item) => item.utcDate >= fromDate && item.utcDate <= toDate
        );
    };

    const formatForecastGraphicReport = (series) => {
        const filteredSeries = filterPerformanceSeriesByCurrentPeriod(series);
        const actualKey = `actual_${forecastMetric}`;
        const forecastKey = `forecast_${forecastMetric}`;
        const isSpentMetric = forecastMetric === 'spent';

        const labels = filteredSeries.map((item) => {
            if (forecastTimezoneMode === 'utc') {
                return `${item.date} ${item.hour}:00`;
            }
            return moment(item.utcDate).format('YYYY-MM-DD HH:00');
        });
        const actualData = filteredSeries.map((item) => item[actualKey]);
        // actual_spent already comes with the user's margin; forecast_spent
        // is raw cost, so it gets the margin to be comparable.
        const userMargin = auth.session?.user?.rtb_margin;
        const forecastData = filteredSeries.map((item) =>
            isSpentMetric
                ? applyMarginToSpend(item[forecastKey], userMargin)
                : item[forecastKey]
        );

        const tooltips = {
            callbacks: {
                label: function(tooltipItems, data) {
                    const index = tooltipItems.datasetIndex;
                    const label = data.datasets[index].label;
                    let value = data.datasets[index].data[tooltipItems.index];

                    if (isSpentMetric) {
                        value =
                            (campaign.currency?.symbol || '') +
                            formatNumber(value, 'currency');
                    } else {
                        value = formatNumber(value, 'rounded');
                    }

                    return `${label}: ${value}`;
                },
            },
        };

        return {
            graphic: {
                labels,
                datasets: [
                    {
                        label: t('sections.dashboard.timeline-actual'),
                        data: actualData,
                        fill: false,
                        backgroundColor: 'rgba(37, 99, 235, 0.9)',
                        borderColor: 'rgba(37, 99, 235, 0.9)',
                        yAxisID: 'y-axis-1',
                    },
                    {
                        label: t('sections.dashboard.timeline-forecast'),
                        data: forecastData,
                        fill: false,
                        backgroundColor: 'rgba(249, 115, 22, 0.9)',
                        borderColor: 'rgba(249, 115, 22, 0.9)',
                        borderDash: [5, 5],
                        yAxisID: 'y-axis-1',
                    },
                ],
            },
            options: {
                maintainAspectRatio: true,
                scales: {
                    yAxes: [
                        {
                            type: 'linear',
                            display: true,
                            position: 'left',
                            id: 'y-axis-1',
                            ticks: isSpentMetric
                                ? {
                                      beginAtZero: true,
                                      callback: function(value) {
                                          if (
                                              dashboard?.selectedCurrency
                                                  ?.short_name
                                          ) {
                                              value = formatNumber(
                                                  value,
                                                  'properCurrency',
                                                  null,
                                                  {
                                                      currency:
                                                          dashboard
                                                              .selectedCurrency
                                                              ?.short_name,
                                                  }
                                              );
                                              value = value.split(',')[0];
                                          }
                                          return value;
                                      },
                                  }
                                : {
                                      beginAtZero: true,
                                  },
                        },
                    ],
                },
                tooltips,
            },
            hasData: filteredSeries.length > 0,
        };
    };

    useEffect(() => {
        if (timelineMode !== 'stats') {
            return;
        }

        if (
            individualCampaignStats.impressions !== '-' &&
            Object.keys(campaign).length > 0
        ) {
            const { graphic, options } = formatGraphicReport(
                individualCampaignStats.days,
                individualCampaignStats.hours
            );
            setGraphicReport(graphic);
            setGraphicOptions(options);
            const reportsTable = formatTableReport();
            if (reportsTable[0].data) {
                setReportsTable(
                    filterOutRowsWithoutProperty(
                        reportsTable,
                        'impressions',
                        'impacts'
                    )
                );
            } else {
                setReportsTable([]);
            }
        }
    }, [individualCampaignStats, campaign, timelineMode]);

    useEffect(() => {
        if (timelineMode !== 'forecast') {
            return;
        }

        const { graphic, options, hasData } = formatForecastGraphicReport(
            campaignPerformanceSeries
        );
        setGraphicReport(graphic);
        setGraphicOptions(options);
        setHasForecastData(hasData);
    }, [
        timelineMode,
        campaignPerformanceSeries,
        forecastMetric,
        forecastTimezoneMode,
        currentPeriod,
    ]);

    const formatGraphicReport = (reportsByDay, reportsByHour) => {
        let subfijo = 'h';
        let labels = [];
        let datasets = [];
        let yAxes = [];

        let reports = [];
        const byHours = isByHourCurrentPeriod(currentPeriod);
        if (byHours) reports = reportsByHour;
        else reports = reportsByDay;

        let reportsCopy;
        if (reports) {
            reportsCopy =
                reports.length > 1 ? [...reports] : [...reports, ...reports];
        }

        // add format to labels of timeline
        const tooltips = {
            callbacks: {
                label: function(tooltipItems, data) {
                    const translatedLabel =
                        t('common.words.spent').toLowerCase();
                    const index = tooltipItems.datasetIndex;
                    const label = data.datasets[index].label;
                    let value = data.datasets[index].data[tooltipItems.index];
                    let withSymbol =
                        label.toLowerCase() == translatedLabel
                            ? campaign.currency.symbol
                            : false;
                    if (withSymbol) {
                        value = formatNumber(value, 'currency');
                    } else {
                        value = formatNumber(value, 'rounded');
                    }
                    return `${label}: ${withSymbol || ''}${value}`;
                },
            },
        };
        if (reportsCopy) {
            reportsCopy.forEach((reprt) => {
                if (byHours) {
                    labels.push(`${reprt.hour}${subfijo}`);
                } else {
                    moment.locale(window.navigator.language);
                    let dateWithMonth = moment(reprt.date, 'YYMMDDHH').format(
                        'L'
                    );
                    dateWithMonth = dateWithMonth.substring(0, 5);
                    labels.push(dateWithMonth);
                }

                Object.keys(reprt).forEach((key, index) => {
                    const translatedLabel = t(
                        'common.words.' + key
                    ).toLowerCase();
                    let dataset = datasets.find(
                        (set) => 'label' in set && set.label === translatedLabel
                    );
                    const keyIsImpressions = key === 'impressions';
                    const keyIsSpent = key === 'spent';
                    const keyIsImpacts = key === 'impacts';
                    let dataWithFormat;
                    switch (key) {
                        case 'impacts':
                            dataWithFormat = parseInt(reprt[key]);
                            break;
                        case 'spent':
                            dataWithFormat = parseFloat(reprt[key]).toFixed(2);
                            break;
                        default:
                            dataWithFormat = reprt[key];
                            break;
                    }

                    if (dataset) {
                        dataset.data = [...dataset.data, dataWithFormat];
                    } else {
                        if (
                            (isDailyCapByBudget && keyIsSpent) ||
                            (!isDailyCapByBudget && keyIsImpressions) ||
                            keyIsImpacts
                        ) {
                            datasets.push({
                                label: translatedLabel,
                                data: [dataWithFormat],
                                fill: keyIsImpacts,
                                backgroundColor: keyIsImpacts
                                    ? 'rgba(54, 162, 235, 0.3)'
                                    : 'rgb(255, 99, 132)',
                                borderColor: keyIsImpacts
                                    ? 'rgba(54, 162, 235, 0.2)'
                                    : 'rgba(255, 99, 132, 0.7)',
                                yAxisID: `y-axis-${index}`,
                            });
                            yAxes.push({
                                type: 'linear',
                                display: true,
                                position: keyIsImpacts ? 'right' : 'left',
                                id: `y-axis-${index}`,
                                gridLines: {
                                    display: keyIsImpacts,
                                },
                                ticks:
                                    key === 'spent'
                                        ? {
                                            beginAtZero: true,
                                            callback: function(value) {
                                                if (
                                                    dashboard
                                                        ?.selectedCurrency
                                                        ?.short_name
                                                ) {
                                                    value = formatNumber(
                                                        value,
                                                        'properCurrency',
                                                        null,
                                                        {
                                                            currency:
                                                                dashboard
                                                                    .selectedCurrency
                                                                    ?.short_name,
                                                        }
                                                    );
                                                    value =
                                                        value.split(',')[0];
                                                }
                                                return value;
                                            },
                                        }
                                        : {
                                            beginAtZero: true,
                                        },
                            });
                        }
                    }
                });
            });
        }
        return {
            //if the campaign hasn't had any impacts, only use the spent.
            graphic: {
                labels,
                datasets: dashboard.individualCampaignStats.impacts
                    ? datasets
                    : datasets.length
                        ? [datasets[0]]
                        : [],
            },
            options: { maintainAspectRatio: true, scales: { yAxes }, tooltips },
        };
    };

    const formatTableReport = () => {
        const reportsTable = JSON.parse(
            JSON.stringify(individualCampaignStats)
        );
        const formatedTable = [
            {
                tabName: t('common.words.screens'),
                columnName: t('common.words.screen'),
                data: reportsTable.screen?.map((row) => ({
                    ...row,
                    name: row.screen,
                    currency: campaign.currency,
                })),
            },
            {
                tabName: capitalizeFirstLetter(t('common.words.days')),
                columnName: capitalizeFirstLetter(t('common.words.day')),
                data: reportsTable.days?.map((row) => {
                    moment.locale(window.navigator.language);
                    let dateWithMonth = moment(row.date, 'YYMMDDHH').format(
                        'L'
                    );
                    dateWithMonth = dateWithMonth.substring(0, 5);
                    return {
                        ...row,
                        name: dateWithMonth,
                        currency: campaign.currency,
                    };
                }),
            },
            {
                tabName: t('common.words.hours'),
                columnName: t('common.words.hour'),
                data: reportsTable.hours?.map((row) => ({
                    ...row,
                    name: row.hour,
                    currency: campaign.currency,
                })),
            },
        ];

        return formatedTable;
    };

    const [screenSizeMatches, setScreenSizeMatches] = useState(
        window.matchMedia('(min-width: 768px)').matches
    );

    useEffect(() => {
        window
            .matchMedia('(min-width: 768px)')
            .addEventListener('change', (e) => setScreenSizeMatches(e.matches));
    }, []);

    useEffect(() => {
        let mounted = true;
        setCampaignScreensStatus({});
        statsService
            .getCampaignsScreensStatus(id)
            .then((res) => {
                if (!mounted) return;
                setCampaignScreensStatus(res.message[0]);
            })
            .catch((err) => {
                if (!mounted) return;
                setCampaignScreensStatus({ error: true });
            });

        return () => {
            mounted = false;
        };
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        let mounted = true;

        statsService.getCampaignPerformance(id, 0).then((response) => {
            if (!mounted) {
                return;
            }

            if (!response) {
                setHasForecastRequestError(true);
                setCampaignPerformanceSeries([]);
                return;
            }

            setHasForecastRequestError(false);
            setCampaignPerformanceSeries(parseCampaignPerformanceSeries(response));
        });

        return () => {
            mounted = false;
        };
    }, [id]);

    return !isCampaignLoaded ? (
        <Loading isLoading={true}></Loading>
    ) : (
        <>
            <Helmet>
                <title>{`${t('sections.dashboard.page-title')} | ${campaign.id
                    } - ${campaign.brand
                        ? campaign.brand
                        : t('sections.campaign-list.without-brand')
                    }`}</title>
            </Helmet>
            <div className="fixed h-screen w-full bg-gray-100 overflow-y-auto pb-10 mt-8 md:mt-0">
                <Header
                    currentSection={t('sections.dashboard.page-title')}
                    sectionPath={'/dashboard'}
                    subSection={`${campaign.brand ? campaign.brand + ' | ' : ''
                        }${campaign.name ? campaign.name : ''}`}
                    campaignId={campaign.id}
                    extraSection={campaign.status}
                    extraIcon={
                        hasPredictions ? (
                            <Icon
                                onClick={() => {
                                    setHandleDataSchedule(!handleDataSchedule);
                                }}
                                name={
                                    hasPendingOrRejected
                                        ? 'exclamation-triangle'
                                        : 'info-circle'
                                }
                                fixedWidth
                                className={`mr-1 ${hasPendingOrRejected && 'text-yellow-500'
                                    } cursor-pointer`}
                            />
                        ) : null
                    }
                />
                <Loading isLoading={isLoading} />
                <DropdownPeriods
                    isDisabled={dashboard.isLoading}
                    fromWholePeriod={wholePeriodFrom}
                    toWholePeriod={wholePeriodTo}
                    handleDropdownPeriods={'DashboardPageIndividual'}
                    currentPeriod={currentPeriod}
                    setCurrentPeriod={setCurrentPeriod}
                    value={value}
                    setValue={setValue}
                    loadingIcon={true}
                />
                {/* Nueva Estructura grid grid-rows-4 md:grid-rows-3 grid-cols-2 md:grid-cols-6 grid-flow-row auto-cols-max gap-4 */}
                <div className={`workingArea mt-4 ${fadeOutAnimation}`}>
                    <div className="flex w-full flex-col md:flex-row my-2 space-y-2 md:space-y-0 md:space-x-2">
                        <DashboardStats
                            currentPeriod={currentPeriod}
                            selectedPeriodName={value}
                            seeImpressions={seeImpressions}
                            statusSlot={
                                hasPredictions ? (
                                    <div className="module flex-1">
                                        <Predictions
                                            campaignScreensStatus={
                                                campaignScreensStatus
                                            }
                                            hasPendingOrRejected={
                                                hasPendingOrRejected
                                            }
                                        />
                                    </div>
                                ) : null
                            }
                        />
                    </div>
                    <div className="row flex flex-col md:flex-row my-2 space-y-2 md:space-y-0 md:space-x-2">
                        <div className="md:w-1/2 module items-center relative overflow-x-auto">
                            <QuickControls />
                        </div>
                        <div className="module md:w-1/2">
                            <CampaignBrief timezone={timezone} />
                        </div>
                    </div>
                    {hasPredictions && (
                        <PredictionDetails
                            setHandleDataSchedule={setHandleDataSchedule}
                            handleDataSchedule={handleDataSchedule}
                            isCampaignPrediction={isCampaignPrediction}
                            campaignPrediction={campaignPrediction}
                            campaign={campaign}
                            isDelayed={isDelayed}
                            campaignScreensStatus={campaignScreensStatus}
                        />
                    )}
                    {isCampaignLoaded &&
                        isEvenlyMode(campaign.scheduler_mode) && (
                            <SpendPacingPanel
                                key={campaign.id}
                                campaign={campaign}
                            />
                        )}
                    <div
                        className={`${dashboard.isSilentLoading || dashboard.isLoading
                                ? 'opacity-50'
                                : 'row flex flex-col md:flex-row my-2 space-y-2 md:space-y-0 md:space-x-2'
                            }`}>
                        <div className="module w-full md:flex-auto">
                            <div className="flex items-center justify-between">
                                <div className="font-light text-xs uppercase">
                                    {t('sections.dashboard.timeline')}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        type="button"
                                        onClick={() => setTimelineMode('stats')}
                                        title={t(
                                            'sections.dashboard.timeline-mode-stats'
                                        )}
                                        className={`h-8 w-8 border rounded-full flex items-center justify-center transition-colors ${
                                            timelineMode === 'stats'
                                                ? 'bg-blue-500 border-blue-500 text-white'
                                                : 'bg-white border-blue-500 text-blue-500'
                                        }`}>
                                        <Icon
                                            name="chart-bar"
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setTimelineMode('forecast')
                                        }
                                        title={t(
                                            'sections.dashboard.timeline-mode-forecast'
                                        )}
                                        className={`h-8 w-8 border rounded-full flex items-center justify-center transition-colors ${
                                            timelineMode === 'forecast'
                                                ? 'bg-blue-500 border-blue-500 text-white'
                                                : 'bg-white border-blue-500 text-blue-500'
                                        }`}>
                                        <Icon
                                            name="chart-line"
                                        />
                                    </button>
                                </div>
                            </div>
                            {timelineMode === 'forecast' && (
                                <div className="mt-2 flex items-center flex-wrap gap-2">
                                    <label className="text-xs font-light uppercase mr-1">
                                        {t('sections.dashboard.timeline-metric')}
                                    </label>
                                    <select
                                        value={forecastMetric}
                                        onChange={(event) =>
                                            setForecastMetric(event.target.value)
                                        }
                                        className="text-xs border border-gray-300 bg-white rounded px-2 py-1 focus:outline-none">
                                        <option value="impacts">
                                            {t('common.words.impacts')}
                                        </option>
                                        <option value="spent">
                                            {t('common.words.spent')}
                                        </option>
                                        {seeImpressions && (
                                            <option value="impressions">
                                                {t('common.words.impressions')}
                                            </option>
                                        )}
                                    </select>
                                    <label className="text-xs font-light uppercase ml-2 mr-1">
                                        {t('sections.dashboard.timeline-timezone')}
                                    </label>
                                    <select
                                        value={forecastTimezoneMode}
                                        onChange={(event) =>
                                            setForecastTimezoneMode(
                                                event.target.value
                                            )
                                        }
                                        className="text-xs border border-gray-300 bg-white rounded px-2 py-1 focus:outline-none">
                                        <option value="utc">
                                            {t(
                                                'sections.dashboard.timeline-timezone-utc'
                                            )}
                                        </option>
                                        <option value="local">
                                            {t(
                                                'sections.dashboard.timeline-timezone-local'
                                            )}
                                        </option>
                                    </select>
                                </div>
                            )}
                            {'datasets' in graphicReport &&
                                'scales' in graphicOptions && (
                                    <Line
                                        width={98}
                                        height={screenSizeMatches ? 30 : 70}
                                        fill={true}
                                        data={graphicReport}
                                        options={graphicOptions}
                                    />
                                )}
                            {timelineMode === 'forecast' && (
                                <div className="text-xs text-gray-500 mt-2">
                                    {t(
                                        'sections.dashboard.timeline-values-timezone',
                                        {
                                            timezone:
                                                getForecastDisplayTimezoneLabel(),
                                        }
                                    )}
                                </div>
                            )}
                            {timelineMode === 'forecast' &&
                                !hasForecastRequestError &&
                                !hasForecastData && (
                                    <div className="text-xs text-gray-500 mt-2 italic">
                                        {t(
                                            'sections.dashboard.timeline-forecast-empty'
                                        )}
                                    </div>
                                )}
                            {timelineMode === 'forecast' &&
                                hasForecastRequestError && (
                                    <div className="text-xs text-red-500 mt-2 italic">
                                        {t(
                                            'sections.dashboard.timeline-forecast-error'
                                        )}
                                    </div>
                                )}
                            <div className="text-xs text-gray-400 mt-2">
                                <b className='text-normal'> {t('sections.dashboard.stats-disclaimer-bold')}</b>
                                <span className='italic'>{t('sections.dashboard.stats-disclaimer')}</span>
                            </div>
                        </div>
                    </div>
                    {isCampaignLoaded &&
                        seeImpressions &&
                        timelineMode === 'forecast' && (
                            <ForecastConfidencePanel
                                key={campaign.id}
                                campaign={campaign}
                                currentPeriod={currentPeriod}
                                requestConfig={getForecastConfidenceRequestConfig(
                                    auth
                                )}
                            />
                        )}
                </div>
                {showReport && (
                    <div className={`workingArea mt-2 ${fadeOutAnimation}`}>
                        <ReportsTable
                            withGraph
                            rowTotals={totalRows}
                            data={reportsTable}
                            currency={campaign.currency}
                            campaignName={campaign.name}
                            seeImpressions={seeImpressions}
                        />
                    </div>
                )}
            </div>
        </>
    );
}

export default DashboardPageIndividual;
